import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthProvider'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { gcsUploadFile } from '@/lib/gcsStorage'
import { Loader2, ArrowLeft, Camera, Save, User, Phone, MapPin, Heart } from 'lucide-react'
import { toast } from 'sonner'

export default function ProfileEditPage() {
  const navigate = useNavigate()
  const { userProfile, refreshUserData } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    job_title: '',
    location: '',
    date_of_birth: '',
    home_address: '',
    home_postcode: '',
    avatar_url: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_mobile_phone: '',
    emergency_contact_home_phone: '',
    emergency_contact_email: '',
  })

  useEffect(() => {
    async function fetchProfile() {
      if (!userProfile?.id) return
      try {
        const { data, error } = await supabase.rpc('app_get_user_profile_detail_v2', {
          p_user_id: userProfile.id,
        })
        if (error) throw error
        if (data) {
          setForm({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            email: data.email || '',
            phone_number: data.phone_number || '',
            job_title: data.job_title || '',
            location: data.location || '',
            date_of_birth: data.date_of_birth || '',
            home_address: data.home_address || '',
            home_postcode: data.home_postcode || '',
            avatar_url: data.avatar_url || '',
            emergency_contact_name: data.emergency_contact_name || '',
            emergency_contact_relationship: data.emergency_contact_relationship || '',
            emergency_contact_mobile_phone: data.emergency_contact_mobile_phone || '',
            emergency_contact_home_phone: data.emergency_contact_home_phone || '',
            emergency_contact_email: data.emergency_contact_email || '',
          })
        }
      } catch (err) {
        console.error('[ProfileEdit] Error:', err)
        toast.error('Failed to load profile')
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [userProfile?.id])

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleAvatarUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/jpeg,image/png,image/webp'
    input.onchange = async (e) => {
      const file = e.target.files?.[0]
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be under 5MB')
        return
      }

      setUploadingAvatar(true)
      try {
        const { publicUrl } = await gcsUploadFile(file, {
          folderPath: `avatars/${userProfile.id}`,
          fileName: `avatar-${Date.now()}.${file.name.split('.').pop()}`,
          contentType: file.type,
        })

        await supabase.rpc('app_update_user_profile_v2', {
          p_user_id: userProfile.id,
          p_avatar_url: `${publicUrl}?t=${Date.now()}`,
        })

        updateField('avatar_url', `${publicUrl}?t=${Date.now()}`)
        toast.success('Photo updated')
        refreshUserData()
      } catch (err) {
        console.error('Avatar upload failed:', err)
        toast.error('Failed to update photo')
      } finally {
        setUploadingAvatar(false)
      }
    }
    input.click()
  }

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('First name and last name are required')
      return
    }

    setSaving(true)
    try {
      const { error } = await supabase.rpc('app_update_user_profile_v2', {
        p_user_id: userProfile.id,
        p_first_name: form.first_name.trim(),
        p_last_name: form.last_name.trim(),
        p_phone_number: form.phone_number.trim() || null,
        p_job_title: form.job_title.trim() || null,
        p_location: form.location.trim() || null,
        p_date_of_birth: form.date_of_birth || null,
        p_home_address: form.home_address.trim() || null,
        p_home_postcode: form.home_postcode.trim() || null,
        p_emergency_contact_name: form.emergency_contact_name.trim() || null,
        p_emergency_contact_relationship: form.emergency_contact_relationship.trim() || null,
        p_emergency_contact_mobile_phone: form.emergency_contact_mobile_phone.trim() || null,
        p_emergency_contact_home_phone: form.emergency_contact_home_phone.trim() || null,
        p_emergency_contact_email: form.emergency_contact_email.trim() || null,
      })

      if (error) throw error
      toast.success('Profile updated')
      refreshUserData()
      navigate('/more')
    } catch (err) {
      console.error('[ProfileEdit] Save error:', err)
      toast.error('Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  const initials = `${form.first_name?.[0] || ''}${form.last_name?.[0] || ''}`

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-5 pb-28">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/more')} className="p-1 -ml-1 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Edit Profile</h1>
      </div>

      {/* Avatar */}
      <div className="flex justify-center">
        <button onClick={handleAvatarUpload} disabled={uploadingAvatar} className="relative group">
          <Avatar className="h-20 w-20">
            <AvatarImage src={form.avatar_url} />
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xl font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity">
            {uploadingAvatar ? (
              <Loader2 className="h-5 w-5 text-white animate-spin" />
            ) : (
              <Camera className="h-5 w-5 text-white" />
            )}
          </div>
        </button>
      </div>

      {/* Personal Info */}
      <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex items-center gap-2 text-gray-700">
          <User className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium">Personal Information</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">First Name</Label>
            <Input value={form.first_name} onChange={e => updateField('first_name', e.target.value)} className="mt-1" required />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Last Name</Label>
            <Input value={form.last_name} onChange={e => updateField('last_name', e.target.value)} className="mt-1" required />
          </div>
        </div>

        <div>
          <Label className="text-xs text-gray-500">Email</Label>
          <Input value={form.email} disabled className="mt-1 bg-gray-50 text-gray-400" />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Job Title</Label>
          <Input value={form.job_title} onChange={e => updateField('job_title', e.target.value)} className="mt-1" placeholder="e.g. Site Manager" />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Date of Birth</Label>
          <Input type="date" value={form.date_of_birth} onChange={e => updateField('date_of_birth', e.target.value)} className="mt-1" />
        </div>
      </section>

      {/* Contact Info */}
      <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex items-center gap-2 text-gray-700">
          <Phone className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium">Contact</span>
        </div>

        <div>
          <Label className="text-xs text-gray-500">Phone Number</Label>
          <Input type="tel" value={form.phone_number} onChange={e => updateField('phone_number', e.target.value)} className="mt-1" placeholder="07..." />
        </div>
      </section>

      {/* Address */}
      <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex items-center gap-2 text-gray-700">
          <MapPin className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium">Address</span>
        </div>

        <div>
          <Label className="text-xs text-gray-500">Location</Label>
          <Input value={form.location} onChange={e => updateField('location', e.target.value)} className="mt-1" placeholder="City or region" />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Home Address</Label>
          <Input value={form.home_address} onChange={e => updateField('home_address', e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Postcode</Label>
          <Input value={form.home_postcode} onChange={e => updateField('home_postcode', e.target.value)} className="mt-1" />
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex items-center gap-2 text-gray-700">
          <Heart className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium">Emergency Contact</span>
        </div>

        <div>
          <Label className="text-xs text-gray-500">Name</Label>
          <Input value={form.emergency_contact_name} onChange={e => updateField('emergency_contact_name', e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Relationship</Label>
          <Input value={form.emergency_contact_relationship} onChange={e => updateField('emergency_contact_relationship', e.target.value)} className="mt-1" placeholder="e.g. Spouse, Parent" />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Mobile Phone</Label>
          <Input type="tel" value={form.emergency_contact_mobile_phone} onChange={e => updateField('emergency_contact_mobile_phone', e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Home Phone</Label>
          <Input type="tel" value={form.emergency_contact_home_phone} onChange={e => updateField('emergency_contact_home_phone', e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Email</Label>
          <Input type="email" value={form.emergency_contact_email} onChange={e => updateField('emergency_contact_email', e.target.value)} className="mt-1" />
        </div>
      </section>

      {/* Sticky save button */}
      <div className="fixed bottom-20 inset-x-0 px-4 pb-safe">
        <Button
          className="w-full bg-emerald-600 hover:bg-emerald-700 h-11 shadow-lg"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>
    </div>
  )
}
